/* assets/diamond-selector.js — Pruden & Smith diamond selector v6 (funnel fixes, 11 Jul 2026)
 * One pop-up with two views:
 *   • ALL    — the full list of stones for this ring
 *   • DETAIL — one stone's spec + the 4Cs guide seeded to that stone
 * PDP shows a 3-stone list (recommended + 2 nearest) + "View all stones".
 *   - PDP row tap / "Details →"   -> opens DETAIL (shopper always sees the spec)
 *   - "View all stones"           -> opens ALL; tile click -> DETAIL
 *   - "Choose this diamond"       -> selects + closes
 * v6 changes (purchase-funnel diagnosis; v6.1 tweaks per Emil 11 Jul):
 *   1. Row tap opens the spec (selection only via "Choose this diamond").
 *   2. "<Metal> Setting £X + Diamond £Y" breakdown line above a clean "Add to Bag | £total" CTA.
 *   3. Re-adding replaces any existing bundle for this ring (no stacking).
 *   4. Proxy failure = hard stop with a message (never silently add the ring alone).
 *   5. Taken stone = confirm modal before switching (never a silent swap).
 *   6. Gallery hover-360 iframes are pointer-events:none so tiles stay clickable.
 * All config comes from data-* on #ps-diamond-selector (set by the liquid).
 * Nivoda is only contacted at add-to-bag (hold) — phase 2.
 */
(function () {
  'use strict';
  if (window.__psDiamondSelectorInit) return; // guard against double-load
  window.__psDiamondSelectorInit = true;

  var allDiamonds = [], matched = [], selected = null, recommended = null;
  var typeFilter = 'all'; // 'all' | 'lab' | 'natural' — the lab/natural toggle
  var ringVariants = {}, variantIdByTitle = {}, holdId = null, addingToCart = false, currentStone = null;
  var metalMedia = {}; // metal title -> the variant's featured media id (for gallery swap)
  var TOP_N = 3;

  function $(id){ return document.getElementById(id); }
  function isLab(d){ return d.labGrown === 'true' || d.labGrown === true; }
  function esc(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;'); }
  function fmtCt(n){ return (Math.round(Number(n)*100)/100).toFixed(2).replace(/\.?0+$/,''); }
  // --- Currency (Shopify Markets) -------------------------------------------
  // Shop/base currency is GBP and the diamond pool stores prices in GBP pence.
  // Verified live 17 Jun 2026: under Markets the ring variant prices (Liquid
  // v.price), product .js and money-rendered prices all arrive ALREADY in the
  // shopper's presentment currency (e.g. EUR rate 1.1792322). The only value
  // not converted is the diamond figure from the metafield — so we convert just
  // that with Shopify.currency.rate and format in the active currency. The cart
  // / checkout remains authoritative for the exact charge.
  function psCur(){ try{ var c=window.Shopify&&window.Shopify.currency; return {active:(c&&c.active)||'GBP', rate:parseFloat(c&&c.rate)||1}; }catch(e){ return {active:'GBP', rate:1}; } }
  function toPresentment(gbpPence){ return Math.round(Number(gbpPence||0)*psCur().rate); } // GBP pence -> presentment minor units
  function fmtPresentmentCents(cents){ var a=psCur().active; var amt=Number(cents||0)/100; try{ return new Intl.NumberFormat(undefined,{style:'currency',currency:a}).format(amt); }catch(e){ return '£'+Math.round(amt).toLocaleString('en-GB'); } }
  function fmtGBP(pence){ if (pence==null) return ''; return fmtPresentmentCents(toPresentment(pence)); } // takes GBP pence, displays in active currency
  function clamp(n,a,b){ return Math.max(a,Math.min(b,n)); }

  // --- 360° viewer (loupe360) -----------------------------------------------
  // loupe360 serves a fixed 500x500 interactive viewer pinned to the page's
  // top-left (drag to rotate the real stone — it is NOT a plain video). We embed
  // it at 500x500 and CSS-scale it down to fill a square box, so it can never be
  // cropped or letterboxed regardless of the box size.
  function make360(url){
    var f=document.createElement('iframe');
    f.className='ds-360'; f.src=url; f.loading='lazy';
    f.setAttribute('title','360° diamond view'); f.setAttribute('scrolling','no');
    f.style.cssText='position:absolute;top:0;left:0;width:500px;height:500px;border:0;transform-origin:top left;background:#edeae5;';
    return f;
  }
  function fit360(box,f){ if(!box||!f) return; var w=box.clientWidth||box.offsetWidth||0; if(w) f.style.transform='scale('+(w/500)+')'; }
  function mount360(box,url,interactive){
    if(!box||!url) return null;
    box.style.position='relative';
    var f=make360(url); box.appendChild(f);
    // Non-interactive (gallery tiles): the iframe is display-only so clicks pass
    // through to the tile button beneath — a cross-origin iframe otherwise swallows
    // every click and the tile becomes un-clickable. Full drag-to-spin lives in the
    // detail view (interactive=true).
    if(!interactive) f.style.pointerEvents='none';
    var fit=function(){ fit360(box,f); };
    f.addEventListener('load',fit); fit();
    if(window.ResizeObserver){ try{ new ResizeObserver(fit).observe(box); }catch(e){} }
    if(interactive){ var h=document.createElement('span'); h.className='ds-360-hint'; h.textContent='Drag to spin ↻'; box.appendChild(h); }
    return f;
  }

  var ROOT, CFG;
  function getCfg(){
    var el = $('ps-diamond-selector'); if (!el) return null;
    var d = el.dataset;
    return {
      shape:(d.ringShape||'ROUND').toUpperCase(), type:(d.ringType||'lab').toLowerCase(),
      productId:d.productId, ringVariantId:d.ringVariantId, apiUrl:(d.apiUrl||'').replace(/\/$/,''), poolKey:d.poolKey||'',
      caratMin:d.caratMin?parseFloat(d.caratMin):null, caratMax:d.caratMax?parseFloat(d.caratMax):null,
      caratIdeal:d.caratIdeal?parseFloat(d.caratIdeal):null,
      ratioMin:d.ratioMin?parseFloat(d.ratioMin):null, ratioMax:d.ratioMax?parseFloat(d.ratioMax):null,
      handSrc:d.handSrc||'', handNativeW:parseFloat(d.handNativeW)||1121,
      fingerPx:parseFloat(d.fingerPx)||258, fingerMm:parseFloat(d.fingerMm)||17,
      centreX:isNaN(parseFloat(d.centreX))?54:parseFloat(d.centreX),
      centreY:isNaN(parseFloat(d.centreY))?52.6:parseFloat(d.centreY),
      rotation:isNaN(parseFloat(d.rotation))?70:parseFloat(d.rotation)
    };
  }

  function filter(cfg){
    // The data is now a shared SHAPE pool (full carat range); slice it to THIS
    // ring's shape, carat range, and (if set) ratio band.
    return allDiamonds.filter(function(d){
      if (!d.shape || d.shape.toUpperCase() !== cfg.shape) return false;
      if (cfg.caratMin !== null && d.carat < cfg.caratMin) return false;
      if (cfg.caratMax !== null && d.carat > cfg.caratMax) return false;
      if (cfg.ratioMin !== null && d.ratio != null && d.ratio < cfg.ratioMin) return false;
      if (cfg.ratioMax !== null && d.ratio != null && d.ratio > cfg.ratioMax) return false;
      return true;
    });
  }
  // Pool stores up to 6 per rung/type for collision fallback; the storefront
  // DISPLAYS at most this many per rung per type. The rest stay in allDiamonds
  // as reserve (kept best-value-first, so we show the best ones).
  var DISPLAY_PER_RUNG = 3;
  function capPerRungType(list, n){
    var seen = {};
    return list.filter(function(d){
      var k = (d.rung==null?d.carat:d.rung) + '|' + (isLab(d)?'L':'N');
      seen[k] = (seen[k]||0) + 1;
      return seen[k] <= n;
    });
  }
  function byPreference(list, cfg){
    if (cfg.caratIdeal == null) return list.slice().sort(function(a,b){return a.carat-b.carat;});
    return list.slice().sort(function(a,b){
      var da=Math.abs(a.carat-cfg.caratIdeal), db=Math.abs(b.carat-cfg.caratIdeal);
      return da!==db ? da-db : a.carat-b.carat;
    });
  }
  // Lab / natural toggle: narrow any list to the chosen type.
  function typed(list){
    if (typeFilter === 'lab') return list.filter(isLab);
    if (typeFilter === 'natural') return list.filter(function(d){ return !isLab(d); });
    return list;
  }
  function topStones(cfg){
    // PDP quick-pick — always the curated "all" view (the lab/natural filter lives
    // only in the gallery). Start at the recommended size, step UP the carat ladder.
    var labs = matched.filter(isLab);
    var pool = labs.length ? labs : matched;
    var asc = pool.slice().sort(function(a,b){ return a.carat - b.carat; });
    var rec = (recommended && pool.indexOf(recommended) !== -1) ? recommended : asc[0];
    var out = rec ? [rec] : [];
    function has(s){ return out.some(function(o){ return o.nivodaId === s.nivodaId; }); }
    function hasCarat(s){ return out.some(function(o){ return o.carat === s.carat; }); }
    for (var i=0;i<asc.length && out.length<TOP_N;i++){
      var s=asc[i];
      if (has(s) || (rec && s.carat < rec.carat) || hasCarat(s)) continue;
      out.push(s);
    }
    for (var j=0;j<asc.length && out.length<TOP_N;j++){
      if (!has(asc[j])) out.push(asc[j]);
    }
    // Always keep at least one natural directly BELOW the recommended lab.
    var nats = matched.filter(function(d){ return !isLab(d); });
    if (nats.length && !out.some(function(s){ return !isLab(s); })){
      var nat = byPreference(nats, cfg)[0];
      if (nat && !has(nat)){ if (out.length >= TOP_N) out[out.length-1] = nat; else out.push(nat); }
    }
    var notRec = function(s){ return !(rec && s.nivodaId===rec.nivodaId); };
    var natRows = out.filter(function(s){ return !isLab(s) && notRec(s); });
    var labRows = out.filter(function(s){ return isLab(s) && notRec(s); }).sort(function(a,b){ return a.carat-b.carat; });
    return (rec?[rec]:[]).concat(natRows).concat(labRows); // recommended, then natural, then labs
  }

  /* ===================== 4Cs visual library ============================= */
  var SHAPES = {
    ROUND:{l:6.5,w:6.5,round:true,cut:'brill',vb:[65,65],d:'M1,32.5 A31.5,31.5 0 1,0 64,32.5 A31.5,31.5 0 1,0 1,32.5 Z'},
    OVAL:{l:7.7,w:5.7,cut:'brill',vb:[57,77],d:'M28.5,1.5 C44,1.5 55,18 55,38.5 C55,59 44,75.5 28.5,75.5 C13,75.5 2,59 2,38.5 C2,18 13,1.5 28.5,1.5 Z'},
    CUSHION:{l:5.6,w:5.6,cut:'brill',vb:[56,56],d:'M2,16 C2,7 7,2 16,2 L40,2 C49,2 54,7 54,16 L54,40 C54,49 49,54 40,54 L16,54 C7,54 2,49 2,40 Z'},
    PRINCESS:{l:5.5,w:5.5,cut:'step',vb:[55,55],d:'M2,2 L53,2 L53,53 L2,53 Z'},
    EMERALD:{l:6.8,w:5.0,cut:'step',vb:[50,68],d:'M9,2 L41,2 L48,9 L48,59 L41,66 L9,66 L2,59 L2,9 Z'},
    PEAR:{l:8.3,w:5.5,cut:'brill',vb:[55,83],d:'M27.5,2 C40,18 53,32 53,54 C53,70 41,81 27.5,81 C14,81 2,70 2,54 C2,32 15,18 27.5,2 Z'},
    MARQUISE:{l:10.0,w:5.0,cut:'brill',vb:[50,100],d:'M25,2 C37,26 48,40 48,50 C48,60 37,74 25,98 C13,74 2,60 2,50 C2,40 13,26 25,2 Z'},
    RADIANT:{l:6.1,w:5.3,cut:'step',vb:[53,61],d:'M8,2 L45,2 L51,8 L51,53 L45,59 L8,59 L2,53 L2,8 Z'},
    ASSCHER:{l:5.5,w:5.5,cut:'step',vb:[55,55],d:'M13,2 L42,2 L53,13 L53,42 L42,53 L13,53 L2,42 L2,13 Z'},
    HEART:{l:6.5,w:6.5,cut:'brill',vb:[65,65],d:'M32.5,61 C31,50 6,41 6,22 C6,11 14,5 22,5 C28,5 32,10 32.5,15 C33,10 37,5 43,5 C51,5 59,11 59,22 C59,41 34,50 32.5,61 Z'}
  };
  var COLOUR=['J','I','H','G','F','E','D'];
  var COLOUR_TXT={D:'Absolutely colourless — the rarest grade, with no hint of tone even under magnification.',E:'Colourless. Only minute traces an expert could find; entirely invisible to the eye.',F:'Colourless. A trace only a gemmologist would detect — exceptionally bright.',G:'Near-colourless. Essentially white face-up; colour shows only beside a higher grade.',H:'Near-colourless. Bright and white in most settings, and lovely in yellow gold.',I:'Near-colourless. A faint warmth that sits beautifully in warmer metals.',J:'Near-colourless. A gentle warmth, usually unnoticed once the stone is set.'};
  var CLAR=['I1','SI2','SI1','VS2','VS1','VVS2','VVS1','IF'];
  var CLAR_TXT={IF:'Internally flawless — no inclusions visible even at 10x magnification.',VVS1:'Very, very slightly included. Inclusions are extremely hard to see at 10x.',VVS2:'Very, very slightly included. Tiny inclusions, only found with effort at 10x.',VS1:'Very slightly included. Minor inclusions, invisible to the naked eye.',VS2:'Very slightly included. Small inclusions, still invisible without magnification.',SI1:'Slightly included. Inclusions seen under magnification, rarely with the eye.',SI2:'Slightly included. Inclusions visible at 10x and occasionally to a keen eye.',I1:'Included. Inclusions visible under magnification and sometimes to the naked eye.'};
  var CUTG=['Fair','Good','Very Good','Excellent'];
  var CUT_TXT={'Fair':'Light escapes through the stone, so it can look a little flat or glassy.','Good':'Returns plenty of light with reasonable sparkle — and good value.','Very Good':'Crisp light return and lively sparkle, very close to the top grade.','Excellent':'Maximum brilliance and fire — light enters and returns to the eye beautifully.'};
  var CARAT_INTRO='A reference for how this carat size looks worn on the hand — carat measures weight, not size.';
  var FOOT='Stones are rendered illustrations, not photographs of the actual diamond. Sizes are approximate for a well-cut stone and scale with the cube root of carat weight; on-screen accuracy depends on your display.';
  // Reused value reassurance, shown in the gallery + each stone's detail.
  var VALUE_LINE='Every stone here is <b>colourless</b> (D–F), <b>eye-clean</b> (VS or better) and <b>Excellent-cut</b> — so you’re choosing between genuinely beautiful diamonds, with none of your budget wasted on detail only a microscope can see.';

  var UID=0;
  function rnd(s){var x=Math.sin(s*99.13)*43758.5453;return x-Math.floor(x);}
  function faceUpStone(shape,cut,colour){
    var s=SHAPES[shape]||SHAPES.ROUND, vw=s.vb[0],vh=s.vb[1],cx=vw/2,cy=vh/2,id=++UID,inner='';
    cut=(cut==null?0.95:cut);colour=colour||0;
    if(s.cut==='step'){var tones=[0.92,0.6,0.78,0.46,0.7];for(var n=0;n<5;n++){var sc=1-n*0.16,L=50+(tones[n]-0.5)*100*(0.4+0.55*cut);inner+='<path d="'+s.d+'" transform="translate('+cx+' '+cy+') scale('+sc.toFixed(3)+') translate('+(-cx)+' '+(-cy)+')" fill="hsl(206,11%,'+L.toFixed(1)+'%)" stroke="rgba(120,140,155,.5)" stroke-width=".6" vector-effect="non-scaling-stroke"/>';}}
    else { inner+=brillFacets(cut,vw,vh,id); }
    var overlay='<g clip-path="url(#d4cclip'+id+')">';
    if(colour>0) overlay+='<rect x="0" y="0" width="'+vw+'" height="'+vh+'" fill="#efe09a" opacity="'+(colour*0.26).toFixed(3)+'"/>';
    overlay+='<ellipse cx="'+(vw*0.34)+'" cy="'+(vh*0.27)+'" rx="'+(vw*0.20)+'" ry="'+(vh*0.10)+'" fill="rgba(255,255,255,.55)"/></g>';
    var prong='',pts=[[cx,vh*0.05],[vw*0.95,cy],[cx,vh*0.95],[vw*0.05,cy]];
    pts.forEach(function(p){prong+='<circle cx="'+p[0]+'" cy="'+p[1]+'" r="'+(Math.min(vw,vh)*0.07)+'" fill="#e9e4d6" stroke="#b9aa86" stroke-width=".5"/>';});
    return '<svg viewBox="0 0 '+vw+' '+vh+'" xmlns="http://www.w3.org/2000/svg" style="overflow:visible"><defs><clipPath id="d4cclip'+id+'"><path d="'+s.d+'"/></clipPath></defs>'+prong+inner+overlay+'<path d="'+s.d+'" fill="none" stroke="#9fb0bd" stroke-width="1.1" vector-effect="non-scaling-stroke"/></svg>';
  }
  function brillFacets(cut,vw,vh,id){
    var cx=50,cy=50,rg=48,rt=23,T=[],fac=[];
    function P(r,deg){var a=(deg-90)*Math.PI/180;return [cx+r*Math.cos(a),cy+r*Math.sin(a)];}
    for(var k=0;k<8;k++)T.push(P(rt,45*k));
    fac.push({pts:T,role:'table',i:0});
    for(k=0;k<8;k++){var m=45*k;fac.push({pts:[T[k],P(rg,m-22.5),P(rg,m),P(rg,m+22.5)],role:'kite',i:k});fac.push({pts:[T[k],T[(k+1)%8],P(rg,m+22.5)],role:'star',i:k});}
    function fill(f){var b;if(f.role==='table')b=0.84;else if(f.role==='kite')b=0.30+0.62*rnd(f.i*1.7+1);else b=0.42+0.5*rnd(f.i*2.3+9);var spread=0.35+0.6*cut,L=Math.max(16,Math.min(99,50+(b-0.5)*100*spread)),hue=206,sat=12,fr=rnd(f.i*5.1+f.role.length);if(fr>0.86){hue=42;sat=22;}else if(fr<0.1){hue=270;sat=20;}return 'hsl('+hue+','+sat+'%,'+L.toFixed(1)+'%)';}
    var g='';
    fac.forEach(function(f){g+='<polygon points="'+f.pts.map(function(p){return p[0].toFixed(1)+','+p[1].toFixed(1);}).join(' ')+'" fill="'+fill(f)+'" stroke="rgba(110,130,145,.30)" stroke-width=".5" vector-effect="non-scaling-stroke"/>';});
    return '<g clip-path="url(#d4cclip'+id+')"><g transform="scale('+(vw/100).toFixed(4)+' '+(vh/100).toFixed(4)+')">'+g+'</g>'+(cut<0.62?'<circle cx="'+(vw/2)+'" cy="'+(vh/2)+'" r="'+(Math.min(vw,vh)*0.26)+'" fill="rgba(150,160,168,'+(0.34*(1-cut)).toFixed(2)+')"/>':'')+'</g>';
  }
  var WVB=[100,96], OUTLINE='M30,8 L70,8 L96,40 L50,92 L4,40 Z';
  var FACETS=['M4,40 L96,40','M18,40 L30,8','M82,40 L70,8','M50,8 L50,40','M30,8 L18,40','M70,8 L82,40','M50,40 L18,40 M50,40 L82,40','M18,40 L50,92','M50,40 L50,92','M82,40 L50,92','M33,40 L50,92','M67,40 L50,92'];
  var SPOTS=[[58,52,1.5],[42,64,1.2],[63,46,1.0],[37,49,1.3],[52,72,1.4],[46,55,1.0],[68,55,1.1],[33,58,1.2],[55,40,0.9]];
  function wireStone(opts){
    opts=opts||{};var tint=opts.tint||0,cut=(opts.cut==null?1:opts.cut),inc=opts.inclusions||0,id=++UID;
    var sm='rgba(70,78,86,'+(0.55+0.40*cut).toFixed(2)+')', sf='rgba(110,124,138,'+(0.30+0.45*cut).toFixed(2)+')';
    var s='<svg viewBox="0 0 '+WVB[0]+' '+WVB[1]+'" xmlns="http://www.w3.org/2000/svg" style="overflow:visible">';
    s+='<defs><clipPath id="d4cw'+id+'"><path d="'+OUTLINE+'"/></clipPath><linearGradient id="d4cy'+id+'" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#f5ecb0"/><stop offset="1" stop-color="#e7cf72"/></linearGradient></defs>';
    if(tint>0) s+='<g clip-path="url(#d4cw'+id+')"><rect x="0" y="0" width="100" height="96" fill="url(#d4cy'+id+')" opacity="'+(tint*0.7).toFixed(3)+'"/></g>';
    if(cut<0.62) s+='<ellipse cx="50" cy="44" rx="20" ry="15" fill="rgba(150,160,168,'+(0.30*(1-cut)).toFixed(2)+')" clip-path="url(#d4cw'+id+')"/>';
    FACETS.forEach(function(p){s+='<path d="'+p+'" fill="none" stroke="'+sf+'" stroke-width="1" vector-effect="non-scaling-stroke"/>';});
    if(cut>0.66){var n=cut>0.95?4:2,spark=[[40,26],[60,24],[50,33],[44,20]];for(var i=0;i<n;i++){var x=spark[i][0],y=spark[i][1],r=2.4;s+='<path d="M'+x+','+(y-r)+' L'+x+','+(y+r)+' M'+(x-r)+','+y+' L'+(x+r)+','+y+'" stroke="#fff" stroke-width="1.4" stroke-linecap="round" vector-effect="non-scaling-stroke" opacity="0.9"/>';}}
    if(inc>0){var cnt=Math.round(inc*SPOTS.length);s+='<g clip-path="url(#d4cw'+id+')">';for(var j=0;j<cnt;j++){var sp=SPOTS[j],rr=sp[2]*(0.6+0.8*inc);s+='<circle cx="'+sp[0]+'" cy="'+sp[1]+'" r="'+rr.toFixed(2)+'" fill="rgba(54,48,42,'+(0.40+0.45*inc).toFixed(2)+')"/>';}s+='</g>';}
    s+='<path d="'+OUTLINE+'" fill="none" stroke="'+sm+'" stroke-width="1.6" stroke-linejoin="round" vector-effect="non-scaling-stroke"/></svg>';
    return s;
  }

  /* ===================== stone object + PDP list ======================== */
  function measFor(d){
    var s = SHAPES[(d.shape||'').toUpperCase()] || SHAPES.ROUND, f = Math.cbrt(d.carat||1);
    var l = d.lengthMm ? Number(d.lengthMm) : s.l*f, w = d.widthMm ? Number(d.widthMm) : s.w*f;
    return s.round ? (w.toFixed(2)+' mm') : (l.toFixed(2)+' × '+w.toFixed(2)+' mm');
  }
  function toStone(d){
    return { nivodaId:d.nivodaId, certNum:d.certNum, certLab:d.certLab, certUrl:d.certUrl,
      type:isLab(d)?'lab':'natural', shape:d.shape, carat:d.carat, colour:d.color, clarity:d.clarity,
      cut:'Excellent', lengthMm:d.lengthMm, widthMm:d.widthMm, depthMm:d.depthMm,
      pricePence:d.price, image:d.image, video:d.video, ratio:d.ratio };
  }
  // PDP row. Tapping the row (.ps-ds-pick) SELECTS the stone; "Details →"
  // (.ps-ds-detail) opens the full-spec pop-out. The selected stone gets a gold
  // tick + gold outline (mirrors the selected metal swatch); the designer's pick
  // keeps its "Recommended" tag wherever it sits.
  function rowHtml(d, isRec, isSel){
    var lab=isLab(d);
    var media = d.image
      ? '<img src="'+esc(d.image)+'" alt="" style="width:54px;height:54px;object-fit:cover;background:#edeae5;flex:none;">'
      : '<div style="width:54px;height:54px;background:#edeae5;display:flex;align-items:center;justify-content:center;color:#9fb8cc;flex:none;">◈</div>';
    var selBadge = isSel ? '<span style="display:inline-block;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#fff;background:#9a7b4f;padding:1px 7px;margin:0 5px 4px 0;">Selected</span>' : '';
    var recBadge = isRec ? '<span style="display:inline-block;font-size:9px;font-weight:700;letter-spacing:.07em;text-transform:uppercase;color:#6b5326;background:#ece8de;padding:1px 7px;margin:0 5px 4px 0;">Recommended</span>' : '';
    var typeBadge = '<span style="display:inline-block;font-size:9px;font-weight:600;letter-spacing:.1em;text-transform:uppercase;color:#211f1b;background:'+(lab?'#ece8de':'#eaddc2')+';padding:2px 7px;margin:0 0 4px 0;">'+(lab?'Lab-Grown':'Natural')+'</span>';
    var spec = fmtCt(d.carat)+'ct - '+esc(d.color||'')+' - '+esc(d.clarity||'')+' | '+measFor(d);
    var tick = isSel
      ? '<span aria-label="Selected" style="width:24px;height:24px;background:#9a7b4f;color:#fff;display:inline-flex;align-items:center;justify-content:center;font-size:14px;flex:none;">&#10003;</span>'
      : '<span aria-hidden="true" style="width:24px;height:24px;flex:none;"></span>';
    var detail = '<button type="button" class="ps-ds-detail" data-detail="'+esc(d.nivodaId)+'" aria-label="View full details" style="flex:none;border:0;background:none;color:#6a645e;font-size:11.5px;letter-spacing:.04em;text-decoration:underline;text-underline-offset:3px;line-height:1;cursor:pointer;padding:6px 2px;white-space:nowrap;">Details &rarr;</button>';
    return '<div class="ps-ds-row'+(isSel?' is-selected':'')+'" style="display:flex;gap:10px;align-items:center;width:100%;border:'+(isSel?'2px solid #9a7b4f':'1px solid #e0dcd6')+';background:#fff;padding:'+(isSel?'9px 11px':'10px 12px')+';margin-bottom:8px;">'+
      '<button type="button" class="ps-ds-pick" data-pick="'+esc(d.nivodaId)+'" aria-label="View this diamond" style="display:flex;gap:12px;align-items:center;flex:1;min-width:0;text-align:left;border:0;background:none;cursor:pointer;padding:0;">'+
        media+'<div style="flex:1;min-width:0;"><div>'+selBadge+recBadge+typeBadge+'</div>'+
          '<div style="font-size:13px;font-weight:600;color:#1a1a1a;">'+spec+'</div>'+
          '<div style="font-size:12px;color:#1a1a1a;font-weight:600;margin-top:3px;">'+fmtGBP(d.price)+'</div></div>'+
      '</button>'+tick+detail+'</div>';
  }
  function tileHtml(d, isRec){
    var lab=isLab(d);
    var media = d.image
      ? '<div class="ds-tile__img"><img src="'+esc(d.image)+'" alt="" style="width:100%;height:100%;object-fit:cover;"></div>'
      : '<div class="ds-tile__img" style="display:flex;align-items:center;justify-content:center;color:#9fb8cc;font-size:22px;">◈</div>';
    var recBadge = isRec ? '<span class="ds-badge" style="background:#9a7b4f;">Recommended</span>' : '';
    var typeBadge = '<span class="ds-badge" style="background:'+(lab?'#ece8de':'#eaddc2')+';color:#211f1b;">'+(lab?'Lab-Grown':'Natural')+'</span>';
    var spec = fmtCt(d.carat)+'ct - '+esc(d.color||'')+' - '+esc(d.clarity||'')+' | '+measFor(d);
    return '<button type="button" class="ds-tile" data-stone="'+esc(d.nivodaId)+'">'+media+
      '<div class="ds-tile__body"><div style="margin-bottom:3px;line-height:1.2;">'+recBadge+typeBadge+'</div>'+
        '<div style="font-size:12.5px;font-weight:600;color:#1a1a1a;">'+spec+'</div>'+
        '<div style="font-size:12px;font-weight:600;color:#1a1a1a;margin-top:2px;">'+fmtGBP(d.price)+'</div></div></button>';
  }
  function bindRows(container){
    // Keep ONE hover 360 viewer alive at a time and swap it on the next hover.
    // (We deliberately don't remove on mouseleave: entering the cross-origin
    // iframe to drag would itself fire mouseleave and kill the viewer mid-spin.)
    var active=null;
    function clear360(){ if(active){ var f=active.querySelector('.ds-360'),h=active.querySelector('.ds-360-hint'); if(f)f.remove(); if(h)h.remove(); active=null; } }
    container.querySelectorAll('[data-stone]').forEach(function(btn){
      var d = allDiamonds.find(function(x){ return x.nivodaId === btn.dataset.stone; });
      btn.addEventListener('click', function(){ if (d) openDetail(toStone(d)); });
      // Interactive 360 on hover (grid tiles only — they have .ds-tile__img).
      var box = d && d.video ? btn.querySelector('.ds-tile__img') : null;
      if(box){
        btn.addEventListener('mouseenter', function(){
          if(active===box) return;
          clear360(); active=box; mount360(box, d.video, false);
        });
      }
    });
  }
  function isSelected(d){ return selected && d.nivodaId===selected.nivodaId; }
  // The displayed 3 = the recommended + nearest, but the SELECTED stone is always
  // shown first (even if the shopper picked it from "view all stones").
  function displayRows(){
    var list = topStones(CFG).slice();
    if (selected && !list.some(function(s){ return s.nivodaId===selected.nivodaId; })) list.unshift(selected);
    if (selected) list.sort(function(a,b){ return (b.nivodaId===selected.nivodaId?1:0)-(a.nivodaId===selected.nivodaId?1:0); });
    return list.slice(0, TOP_N);
  }
  function bindPickRows(container){
    // Tapping a row (or "Details →") opens the full-spec pop-out — every shopper
    // sees the stone's spec before committing. Selection happens ONLY via
    // "Choose this diamond" in that view (Emil's call, 11 Jul: seeing the spec
    // matters more than one-tap selection).
    container.querySelectorAll('[data-pick],[data-detail]').forEach(function(b){
      var id = b.getAttribute('data-pick') || b.getAttribute('data-detail');
      var d = allDiamonds.find(function(x){ return x.nivodaId === id; });
      b.addEventListener('click', function(e){ e.stopPropagation(); if (d) openDetail(toStone(d)); });
    });
  }
  function renderList(){
    var box=$('ps-ds-list'); if(!box) return;
    box.innerHTML = displayRows().map(function(d){ return rowHtml(d, recommended && d.nivodaId===recommended.nivodaId, isSelected(d)); }).join('');
    bindPickRows(box);
  }
  /* ---- lab / natural filter (square, on-brand segmented toggle) — gallery only ---- */
  function typeBtnStyle(active){
    return 'font:inherit;font-size:11px;letter-spacing:.06em;text-transform:uppercase;padding:7px 13px;border:0;cursor:pointer;background:'+(active?'#9a7b4f':'#fff')+';color:'+(active?'#fff':'#3a352f')+';';
  }
  function hasBothTypes(){ return matched.some(isLab) && matched.some(function(d){ return !isLab(d); }); }
  function buildTypeToggle(){
    var labels=[['all','All'],['lab','Lab-grown'],['natural','Natural']];
    var wrap=document.createElement('div'); wrap.className='ps-ds-typefilter';
    wrap.style.cssText='display:inline-flex;border:1px solid #d8d2c8;';
    labels.forEach(function(p,i){
      var b=document.createElement('button'); b.type='button'; b.className='ps-ds-typebtn'; b.setAttribute('data-type',p[0]); b.textContent=p[1];
      b.style.cssText=typeBtnStyle(p[0]===typeFilter)+(i?'border-left:1px solid #d8d2c8;':'');
      b.addEventListener('click', function(){ setFilter(p[0]); });
      wrap.appendChild(b);
    });
    return wrap;
  }
  function renderFilterToggles(){
    [].forEach.call(document.querySelectorAll('.ps-ds-typebtn'), function(b){
      var active=b.getAttribute('data-type')===typeFilter;
      b.style.background=active?'#9a7b4f':'#fff'; b.style.color=active?'#fff':'#3a352f';
    });
  }
  function setFilter(val){
    if (val===typeFilter) return;
    typeFilter = val;                  // filters the GALLERY grid only; PDP list is unaffected
    renderFilterToggles();
    if (overlay && overlay.classList.contains('is-open') && !viewAll.hidden) renderAllGrid();
  }

  /* ===================== modal (ALL <-> DETAIL) ========================= */
  var overlay, viewAll, viewDetail, backBtn, titleEl, lastFocus, allSort='carat-asc';
  function openOverlay(){ overlay.hidden=false; overlay.classList.add('is-open'); document.body.style.overflow='hidden'; }
  function closeOverlay(){ overlay.classList.remove('is-open'); overlay.hidden=true; document.body.style.overflow=''; if(lastFocus&&lastFocus.focus) lastFocus.focus(); }

  function sortStones(list){
    var s=list.slice();
    if(allSort==='carat-desc') return s.sort(function(a,b){return (b.carat||0)-(a.carat||0);});
    if(allSort==='price-asc')  return s.sort(function(a,b){return (a.price||0)-(b.price||0);});
    if(allSort==='price-desc') return s.sort(function(a,b){return (b.price||0)-(a.price||0);});
    return s.sort(function(a,b){return (a.carat||0)-(b.carat||0);}); // carat-asc (default)
  }
  function renderAllGrid(){
    var grid=viewAll.querySelector('.ds-all-grid'); if(!grid) return;
    var list = typed(matched);
    grid.innerHTML = sortStones(list).map(function(d){ return tileHtml(d, recommended && d.nivodaId===recommended.nivodaId); }).join('');
    bindRows(viewAll);
    var c=viewAll.querySelector('#ds-all-count'); if(c) c.textContent = list.length+' diamond'+(list.length===1?'':'s')+' designer selected for this design.';
  }
  function openAll(){
    lastFocus = document.activeElement;
    titleEl.textContent = 'Choose your diamond';
    backBtn.hidden = true;
    viewDetail.hidden = true; viewAll.hidden = false;
    viewAll.innerHTML =
      '<div class="ds-all-head" style="display:flex;justify-content:space-between;align-items:center;gap:12px;flex-wrap:wrap;margin:0 0 14px;">'+
        '<p id="ds-all-count" style="margin:0;font-size:12.5px;color:#6a645e;"></p>'+
        '<div style="display:flex;align-items:center;gap:12px;flex-wrap:wrap;">'+
          '<span class="ds-typefilter-slot"></span>'+
          '<label style="font-size:12px;color:#6a645e;display:inline-flex;align-items:center;gap:6px;white-space:nowrap;">Sort'+
            '<select class="ds-sort" style="font:inherit;font-size:12px;padding:6px 8px;border:1px solid #d8d2c8;background:#fff;color:#1a1a1a;cursor:pointer;">'+
              '<option value="carat-asc">Carat: low to high</option>'+
              '<option value="carat-desc">Carat: high to low</option>'+
              '<option value="price-asc">Price: low to high</option>'+
              '<option value="price-desc">Price: high to low</option>'+
            '</select>'+
          '</label>'+
        '</div>'+
      '</div>'+
      '<div class="ds-value">'+VALUE_LINE+'</div>'+
      '<div class="ds-all-grid"></div>';
    var slot=viewAll.querySelector('.ds-typefilter-slot'); if(slot && hasBothTypes()) slot.appendChild(buildTypeToggle());
    var sel=viewAll.querySelector('.ds-sort');
    if(sel){ sel.value=allSort; sel.addEventListener('change', function(){ allSort=this.value; renderAllGrid(); }); }
    renderAllGrid();
    overlay.querySelector('.ds-modal__body').scrollTop = 0;
    openOverlay();
  }

  function openDetail(stone){
    if (!overlay.classList.contains('is-open')) lastFocus = document.activeElement;
    currentStone = stone;
    titleEl.textContent = '';
    backBtn.hidden = false;
    viewAll.hidden = true; viewDetail.hidden = false;
    viewDetail.innerHTML = detailHtml(stone);
    wireDetail(stone);
    overlay.querySelector('.ds-modal__body').scrollTop = 0;
    openOverlay();
  }

  /* ---- DETAIL view = spec header + 4Cs (seeded, interactive) ---- */
  var st = {}; // 4Cs working state for the open stone
  function seed(stone){
    st.shape = (stone.shape && SHAPES[String(stone.shape).toUpperCase()]) ? String(stone.shape).toUpperCase() : CFG.shape;
    if(!SHAPES[st.shape]) st.shape='ROUND';
    st.carat = clamp(parseFloat(stone.carat)|| (CFG.caratIdeal||1), CFG.caratMin||0.3, CFG.caratMax||5);
    st.colour = COLOUR.indexOf(String(stone.colour||'').toUpperCase()); if(st.colour<0) st.colour=COLOUR.indexOf('E');
    st.clarity = CLAR.indexOf(String(stone.clarity||'').toUpperCase()); if(st.clarity<0) st.clarity=CLAR.indexOf('VS1');
    st.cut = CUTG.indexOf(stone.cut||'Excellent'); if(st.cut<0) st.cut=CUTG.indexOf('Excellent');
  }
  function specHtml(stone){
    var s=SHAPES[st.shape]||SHAPES.ROUND, f=Math.cbrt(st.carat);
    var lmm=stone.lengthMm?Number(stone.lengthMm):s.l*f, wmm=stone.widthMm?Number(stone.widthMm):s.w*f;
    var dmm=stone.depthMm?Number(stone.depthMm):(s.round?wmm*0.615:wmm*0.63);
    var lab=stone.type==='lab';
    // 360 viewer is mounted by wireDetail() (needs a live box to scale into);
    // here we just emit the box, carrying the url on data-vid.
    var mediaInner = stone.video ? '' : (stone.image?'<img src="'+esc(stone.image)+'" alt="">':faceUpStone(st.shape,0.95,0.10));
    var mediaAttr = stone.video ? ' data-vid="'+esc(stone.video)+'"' : '';
    var title=st.carat.toFixed(2)+'ct '+(st.shape.charAt(0)+st.shape.slice(1).toLowerCase())+' '+(lab?'Lab-Grown':'Natural')+' Diamond';
    var cert=stone.certNum?((stone.certLab||'')+' #'+stone.certNum):(stone.certLab||'');
    function row(k,v){return '<tr><td>'+k+'</td><td>'+v+'</td></tr>';}
    // Ratio is only meaningful on elongated shapes — show it (and reassure) there only.
    var ELONG_SHAPES=['OVAL','EMERALD','PEAR','MARQUISE','RADIANT'];
    var rv=stone.ratio?Number(stone.ratio):(lmm&&wmm?lmm/wmm:null);
    var isElong=ELONG_SHAPES.indexOf(st.shape)>=0||(st.shape==='CUSHION'&&rv&&rv>=1.08);
    var ratioRow=(isElong&&rv)?row('Ratio',rv.toFixed(2)+' : 1'):'';
    var ratioNote=(isElong&&rv)?'<p class="ds-ratio-note">Its <b>'+rv.toFixed(2)+' : 1</b> length-to-width ratio is hand-picked for a flattering, balanced outline — elegant on the hand, never stubby or too narrow.</p>':'';
    var rows=row('Shape',st.shape.charAt(0)+st.shape.slice(1).toLowerCase())+row('Carat',st.carat.toFixed(2)+'ct')+row('L × W × D',lmm.toFixed(2)+' × '+wmm.toFixed(2)+' × '+dmm.toFixed(2)+' mm')+ratioRow+row('Colour',COLOUR[st.colour])+row('Clarity',CLAR[st.clarity])+row('Cut',CUTG[st.cut])+row('Polish','Excellent')+row('Symmetry','Excellent')+(cert?row('Certificate',esc(cert)):'');
    var certBtn=stone.certUrl?'<button type="button" class="d4c-certbtn" data-cert-url="'+esc(stone.certUrl)+'">View diamond certificate ↗</button>':'';
    var price=stone.pricePence?(' | '+fmtGBP(stone.pricePence)):'';
    return '<div class="d4c-spec"><div class="d4c-spec__img"'+mediaAttr+'>'+mediaInner+'</div><div>'+
      '<span class="d4c-spec__badge d4c-spec__badge--'+(lab?'lab':'natural')+'">'+(lab?'Lab-Grown':'Natural')+'</span>'+
      '<div class="d4c-spec__title">'+esc(title)+'</div><table>'+rows+'</table>'+
      '<div class="ds-value">'+VALUE_LINE+'</div>'+ratioNote+
      '<button type="button" class="d4c-choose" data-d4c-choose>Choose this diamond'+price+'</button>'+certBtn+'</div></div>';
  }
  function cardCCC(key,label,arr){
    var ticks=arr.map(function(g){return '<span>'+g+'</span>';}).join('');
    return '<div class="d4c-card"><div class="d4c-card__t">'+label+' — <b data-'+key+'-val></b></div>'+
      '<div class="d4c-card__d" data-'+key+'-desc></div><div class="d4c-stage"><div data-'+key+'-stone style="width:100%;display:flex;justify-content:center"></div></div>'+
      '<div class="d4c-sld"><input type="range" data-'+key+'-sld min="0" max="'+(arr.length-1)+'" step="1" aria-label="'+label+'"><div class="d4c-ticks" data-'+key+'-ticks>'+ticks+'</div></div></div>';
  }
  function detailHtml(stone){
    seed(stone);
    var caratTicks='<span>'+(CFG.caratMin||0.5)+' ct</span><span>ideal '+(CFG.caratIdeal||'')+' ct</span><span>'+(CFG.caratMax||5)+' ct</span>';
    var heroStone='<div class="d4c-hand__stone" data-hand-stone></div>';
    var handInner=CFG.handSrc?'<img src="'+esc(CFG.handSrc)+'" alt="Diamond on a hand" loading="lazy">'+heroStone:'<div style="aspect-ratio:1121/1403;display:flex;align-items:center;justify-content:center;color:#6b665d;font-size:12px;padding:20px;text-align:center">Add diamond-4cs-hand.jpg to theme Assets to show the stone at size.</div>'+heroStone;
    var caratTile='<div class="d4c-card d4c-card--carat"><div class="d4c-card__t">Carat — <b><span data-c-val>'+st.carat.toFixed(2)+'</span>ct</b> <span class="d4c-mm-t" data-c-mm></span></div>'+
      '<div class="d4c-card__d">'+CARAT_INTRO+'</div>'+
      '<div class="d4c-hand" data-hand-wrap>'+handInner+'</div>'+
      '<div class="d4c-sld"><input type="range" data-c-sld min="'+(CFG.caratMin||0.3)+'" max="'+(CFG.caratMax||5)+'" step="0.01" value="'+st.carat+'"><div class="d4c-ticks">'+caratTicks+'</div></div></div>';
    return specHtml(stone)+
      '<div class="d4c-eyebrow" style="margin:6px 0 10px;">Explore the 4 Cs of this diamond</div>'+
      '<div class="d4c-grid3">'+caratTile+cardCCC('cut','Cut',CUTG)+cardCCC('colour','Colour',COLOUR)+cardCCC('clarity','Clarity',CLAR)+'</div>'+
      '<p class="d4c-foot">'+FOOT+'</p>';
  }
  function q(sel){ return viewDetail.querySelector(sel); }
  function setFill(sel,frac){ var el=q(sel); if(el) el.style.setProperty('--fill',(clamp(frac,0,1)*100)+'%'); }
  function markTicks(key,idx){ var t=q('[data-'+key+'-ticks]'); if(t)[].forEach.call(t.children,function(sp,i){sp.classList.toggle('is-active',i===idx);}); }
  function drawHand(){
    var s=SHAPES[st.shape],f=Math.cbrt(st.carat),wmm=s.w*f,lmm=s.l*f;
    var cv=q('[data-c-val]'); if(cv) cv.textContent=st.carat.toFixed(2);
    var mm=q('[data-c-mm]'); if(mm) mm.textContent=s.round?('≈ '+wmm.toFixed(1)+' mm'):('≈ '+lmm.toFixed(1)+' × '+wmm.toFixed(1)+' mm');
    setFill('[data-c-sld]',(st.carat-(CFG.caratMin||0.3))/((CFG.caratMax||5)-(CFG.caratMin||0.3)));
    var stoneEl=q('[data-hand-stone]');
    if(stoneEl){ stoneEl.innerHTML = faceUpStone(st.shape,0.95,0.12); }
    placeHand();
  }
  function placeHand(){
    var wrap=q('[data-hand-wrap]'), stoneEl=q('[data-hand-stone]'); if(!wrap||!stoneEl) return;
    // Scale to the IMAGE's actual rendered width (not the wrap, which can be wider
    // mid-open) so the stone stays life-size relative to the finger in the photo.
    var img=wrap.querySelector('img');
    var imgW=(img && img.getBoundingClientRect().width) || wrap.clientWidth || 400;
    if(!imgW) return;
    var lifePpm=(CFG.fingerPx/CFG.fingerMm)*imgW/CFG.handNativeW, s=SHAPES[st.shape],f=Math.cbrt(st.carat),wmm=s.w*f;
    stoneEl.style.width=(wmm*lifePpm)+'px'; stoneEl.style.left=CFG.centreX+'%'; stoneEl.style.top=CFG.centreY+'%';
    stoneEl.style.transform='translate(-50%,-50%) rotate('+CFG.rotation+'deg)';
  }
  function drawCut(){var g=CUTG[st.cut],v=q('[data-cut-val]'),de=q('[data-cut-desc]'),sn=q('[data-cut-stone]');if(v)v.textContent=g;if(de)de.textContent=CUT_TXT[g];if(sn)sn.innerHTML=wireStone({cut:st.cut/3});setFill('[data-cut-sld]',st.cut/(CUTG.length-1));markTicks('cut',st.cut);}
  function drawCol(){var g=COLOUR[st.colour],v=q('[data-colour-val]'),de=q('[data-colour-desc]'),sn=q('[data-colour-stone]');if(v)v.textContent=g;if(de)de.textContent=COLOUR_TXT[g];var amt=(COLOUR.length-1-st.colour)/(COLOUR.length-1);if(sn)sn.innerHTML=wireStone({cut:0.95,tint:amt});setFill('[data-colour-sld]',st.colour/(COLOUR.length-1));markTicks('colour',st.colour);}
  function drawClar(){var g=CLAR[st.clarity],v=q('[data-clarity-val]'),de=q('[data-clarity-desc]'),sn=q('[data-clarity-stone]');if(v)v.textContent=g;if(de)de.textContent=CLAR_TXT[g];var amt=(CLAR.length-1-st.clarity)/(CLAR.length-1);if(sn)sn.innerHTML='<div style="position:relative;width:100%;display:flex;justify-content:center">'+wireStone({cut:0.95,inclusions:amt})+'<div class="d4c-loupe"><div>'+wireStone({cut:0.95,inclusions:amt})+'</div></div></div>';setFill('[data-clarity-sld]',st.clarity/(CLAR.length-1));markTicks('clarity',st.clarity);}
  function wireDetail(stone){
    q('[data-c-sld]').addEventListener('input',function(){st.carat=parseFloat(this.value);drawHand();});
    q('[data-cut-sld]').addEventListener('input',function(){st.cut=+this.value;drawCut();});
    q('[data-colour-sld]').addEventListener('input',function(){st.colour=+this.value;drawCol();});
    q('[data-clarity-sld]').addEventListener('input',function(){st.clarity=+this.value;drawClar();});
    q('[data-cut-sld]').value=st.cut; q('[data-colour-sld]').value=st.colour; q('[data-clarity-sld]').value=st.clarity;
    var ch=q('[data-d4c-choose]'); if(ch) ch.addEventListener('click', function(){ chooseStone(stone); });
    var cb=q('[data-cert-url]'); if(cb) cb.addEventListener('click', function(){ var u=cb.getAttribute('data-cert-url'); if(u) window.open(u,'_blank','noopener'); });
    var hWrap=q('[data-hand-wrap]');
    var hImg=hWrap && hWrap.querySelector('img'); if(hImg){ if(hImg.complete) placeHand(); hImg.addEventListener('load', placeHand); }
    // Re-fit the stone once the modal's 2-column grid settles (placeHand can first
    // run while the wrap is momentarily wider, which oversizes the stone).
    if(hWrap && window.ResizeObserver){ try{ new ResizeObserver(function(){ placeHand(); }).observe(hWrap); }catch(e){} }
    setTimeout(placeHand, 120); setTimeout(placeHand, 400);
    var vbox=q('.d4c-spec__img[data-vid]'); if(vbox) mount360(vbox, vbox.getAttribute('data-vid'), true);
    drawHand(); drawCut(); drawCol(); drawClar();
  }

  /* ===================== selection + add to bag ========================= */
  function chooseStone(stone){
    selected = allDiamonds.find(function(x){ return x.nivodaId === stone.nivodaId; }) || null;
    closeOverlay();
    var actions=$('ps-ds-actions'); if(actions) actions.style.display='';
    renderList();          // reflect the choice as a tick in the list (no faint text line)
    updateAddLabel();
  }
  function getRingVariant(){ var f=document.querySelector('form[action="/cart/add"]'); var v=f&&f.querySelector('input[name="id"]'); return (v&&v.value)?v.value:CFG.ringVariantId; }
  function currentRingPrice(){ var f=document.querySelector('form[action="/cart/add"]'); var vid=f&&f.querySelector('input[name="id"]')?f.querySelector('input[name="id"]').value:null; if(!vid) vid=($('ps-diamond-selector')||{dataset:{}}).dataset.ringVariantId; var p=ringVariants[vid]; return typeof p==='number'?p:0; }
  function selectedMetal(){
    var r=document.querySelector('input[type="radio"][name^="options["]:checked');
    return r && r.value ? String(r.value) : '';
  }
  function updateAddLabel(){
    // CTA stays a clean "Add to Bag | £total"; the line above spells out what the
    // total is made of — "Platinum Setting £3,200 + Diamond £4,570" — so the price
    // is never a mystery number.
    var btn=$('ps-ds-add-btn'); if(!btn) return;
    var ring=currentRingPrice(), total=ring+(selected?toPresentment(selected.price||0):0);
    btn.textContent=total>0?('Add to Bag | '+fmtPresentmentCents(total)):'Add to Bag';
    var bd=$('ps-ds-breakdown');
    if(!bd && btn.parentNode){ bd=document.createElement('div'); bd.id='ps-ds-breakdown'; bd.style.cssText='font-size:12px;color:#6a645e;margin:0 0 8px;text-align:center;'; btn.parentNode.insertBefore(bd,btn); }
    if(bd){
      // Names both halves of the price: "Platinum Setting £3,200 + 1.5ct Emerald Natural Diamond £4,570"
      var metal=selectedMetal();
      var sh=String((selected&&selected.shape)||CFG.shape||''); sh=sh?sh.charAt(0).toUpperCase()+sh.slice(1).toLowerCase():'';
      var stoneName=selected?(fmtCt(selected.carat)+'ct '+(sh?sh+' ':'')+(isLab(selected)?'Lab-Grown':'Natural')+' Diamond'):'';
      bd.textContent = (selected && ring>0) ? ((metal?metal+' ':'')+'Setting '+fmtPresentmentCents(ring)+'  +  '+stoneName+' '+fmtGBP(selected.price)) : '';
      bd.style.display=(selected && ring>0)?'':'none';
    }
  }
  async function postJson(url,body){ var r=await fetch(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)}); if(!r.ok) throw new Error('req '+r.status); return r.json(); }
  // The ring's display name — lets the proxy title the triggered diamond product
  // "… — for <Ring Name>" so the cart pairing is obvious.
  function ringTitle(){ var h=document.querySelector('.product__title h1, h1.product__title, h1'); return h?h.textContent.replace(/\s+/g,' ').trim().slice(0,80):''; }

  /* ---- self-heal: when a chosen stone is taken, drop it + switch to the closest live one ---- */
  function removeStone(stone){
    function drop(arr){ var i=arr.indexOf(stone); if(i>-1) arr.splice(i,1); }
    drop(allDiamonds); drop(matched);
  }
  function nextClosest(prev){
    var pool = matched.filter(function(d){ return d!==prev; });
    if(!pool.length) return null;
    var labs = pool.filter(isLab);
    return byPreference(labs.length?labs:pool, CFG)[0] || null; // nearest-to-ideal, prefer lab
  }
  function reflectSelection(){
    var labs = matched.filter(isLab);
    recommended = byPreference(labs.length?labs:matched, CFG)[0] || null;
    renderList();
    updateAddLabel();
  }
  function stoneLabel(s){ return fmtCt(s.carat)+'ct '+CFG.shape+' '+(isLab(s)?'Lab-Grown':'Natural')+' '+(s.color||'')+'/'+(s.clarity||''); }
  function dsNotify(msg,isError){
    var actions=$('ps-ds-actions'); if(!actions){ alert(msg); return; }
    var box=$('ps-ds-notice');
    if(!box){ box=document.createElement('div'); box.id='ps-ds-notice'; actions.insertBefore(box, actions.firstChild); }
    box.textContent=msg;
    box.style.cssText='font-size:12.5px;line-height:1.5;padding:9px 12px;margin:0 0 12px;border-left:3px solid '+(isError?'#a23b2e':'#9a7b4f')+';background:#faf8f4;color:#3a352f;';
    box.style.display='';
  }
  function clearNotice(){ var box=$('ps-ds-notice'); if(box) box.style.display='none'; }
  function reportUnavailable(stone){
    if(!CFG.apiUrl || !CFG.poolKey || !stone || !stone.certNum) return;
    // best-effort, cross-user prune — fine if the endpoint isn't deployed yet
    try{ fetch(CFG.apiUrl+'/api/diamond/mark-unavailable',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({certNumber:stone.certNum,poolKey:CFG.poolKey}),keepalive:true}); }catch(e){}
  }

  // Ask before switching stones — never quietly put a different diamond in the bag.
  function askSwitch(next){
    return new Promise(function(resolve){
      var ov=document.createElement('div');
      ov.style.cssText='position:fixed;inset:0;background:rgba(20,18,15,.55);z-index:99999;display:flex;align-items:center;justify-content:center;padding:20px;';
      ov.innerHTML='<div style="background:#fcfbf9;max-width:430px;width:100%;padding:24px 22px;box-shadow:0 10px 40px rgba(0,0,0,.25);">'+
        '<div style="font-size:15px;font-weight:600;color:#1a1a1a;margin-bottom:8px;">That diamond was just reserved</div>'+
        '<p style="font-size:13px;line-height:1.55;color:#3a352f;margin:0 0 14px;">Another customer has just reserved the stone you picked. The closest available match is:<br><b>'+esc(stoneLabel(next))+' &mdash; '+fmtGBP(next.price)+'</b></p>'+
        '<div style="display:flex;gap:10px;">'+
          '<button type="button" data-ps-use style="flex:1;background:#211f1b;color:#fff;border:0;padding:13px 8px;cursor:pointer;font:inherit;font-size:12.5px;letter-spacing:.05em;text-transform:uppercase;">Use this stone</button>'+
          '<button type="button" data-ps-pick style="flex:1;background:#fff;color:#211f1b;border:1px solid #211f1b;padding:13px 8px;cursor:pointer;font:inherit;font-size:12.5px;letter-spacing:.05em;text-transform:uppercase;">Pick another</button>'+
        '</div></div>';
      document.body.appendChild(ov);
      ov.querySelector('[data-ps-use]').addEventListener('click',function(){ ov.remove(); resolve(true); });
      ov.querySelector('[data-ps-pick]').addEventListener('click',function(){ ov.remove(); resolve(false); });
    });
  }
  // A retry replaces the previous ring+diamond pair for THIS design — bundles never
  // stack (Kate's cart + Emil's test both ended up with multiple full bundles).
  async function replaceExistingBundle(){
    try{
      var cart=await (await fetch('/cart.js',{headers:{'Accept':'application/json'}})).json();
      var refs={};
      (cart.items||[]).forEach(function(i){ var p=i.properties||{}; if(!p._bundle_ref) return;
        if(String(i.product_id)===String(CFG.productId) || String(p._paired_ring)===String(CFG.productId)) refs[p._bundle_ref]=1; });
      var updates={}, holds=[];
      (cart.items||[]).forEach(function(i){ var p=i.properties||{};
        if(p._bundle_ref && refs[p._bundle_ref]){ updates[i.key]=0; if(p._hold_id) holds.push(p._hold_id); } });
      if(Object.keys(updates).length){
        holds.forEach(function(h){ try{ fetch(CFG.apiUrl+'/api/diamond/cancel-hold',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({holdId:h}),keepalive:true}); }catch(e){} });
        await fetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates:updates})});
      }
    }catch(e){ /* best-effort — worst case the shopper sees two bundles, as before */ }
  }

  // Reserving takes ~15-30s (live Nivoda check + hold + product creation). A frozen
  // button reads as broken; cycle honest progress messages that set the expectation.
  function startProgress(btn){
    if(!btn) return { stop: function(){} };
    var msgs=['Checking this stone is still available…','Placing your 24-hour hold…','Securing your diamond — this takes about 20 seconds…','Nearly there — confirming your reservation…'];
    var i=0; btn.textContent=msgs[0];
    var t=setInterval(function(){ i=Math.min(i+1,msgs.length-1); btn.textContent=msgs[i]; }, 6000);
    return { stop: function(){ clearInterval(t); } };
  }

  async function addToBag(){
    if(addingToCart) return; if(!selected){ alert('Please choose a diamond first.'); return; }
    var sizeSel=$('ps-ds-size'), ringSize=sizeSel?sizeSel.value:'';
    if(sizeSel && !ringSize){ alert('Please select your ring size.'); var t=$('ps-ds-size-trigger'); if(t)t.focus(); return; }
    var btn=$('ps-ds-add-btn'); addingToCart=true; clearNotice(); if(btn){btn.disabled=true;}
    var prog=startProgress(btn);
    var bundleRef='ps-'+Date.now(), prep=null;
    function bail(msg){ prog.stop(); if(btn){btn.disabled=false;} addingToCart=false; reflectSelection(); if(msg) dsNotify(msg,true); }
    if(CFG.apiUrl){
      // Reserve the chosen stone; if it was just taken, offer (never force) the
      // closest available alternative, up to a few hops.
      for(var attempt=0; attempt<6; attempt++){
        try{ prep=await postJson(CFG.apiUrl+'/api/diamond/prepare',{certNumber:selected.certNum,lab:isLab(selected),ringProductId:CFG.productId,ringTitle:ringTitle(),poolKey:CFG.poolKey}); }
        catch(e){
          // One quiet retry, then a hard stop — NEVER silently add the ring without
          // its diamond (the old preview fallback did, and customers paid for it).
          try{ await new Promise(function(r){ setTimeout(r,1500); });
               prep=await postJson(CFG.apiUrl+'/api/diamond/prepare',{certNumber:selected.certNum,lab:isLab(selected),ringProductId:CFG.productId,ringTitle:ringTitle(),poolKey:CFG.poolKey}); }
          catch(e2){ bail('Sorry — we couldn’t reserve your diamond just now. Please try again in a minute, or book an appointment and we’ll help you directly.'); return; }
        }
        if(prep && prep.available!==false) break; // got a live, held stone
        reportUnavailable(selected);            // tell the proxy so others stop seeing it
        removeStone(selected);
        var next=nextClosest(selected);
        if(!next){ bail('Sorry — the diamonds for this design have just sold. Please check back shortly, or book an appointment and we’ll source the perfect stone for you.'); return; }
        var useIt=await askSwitch(next);
        selected=next; reflectSelection();
        if(!useIt){ prog.stop(); if(btn){btn.disabled=false;} addingToCart=false; openAll(); return; } // they'd rather browse
      }
      if(!prep || prep.available===false){ bail('Sorry — these diamonds are selling fast and the few we tried just went. Please refresh and try again, or book an appointment and we’ll source the perfect stone for you.'); return; }
      await replaceExistingBundle(); // swap out any earlier pair for this ring
    }
    prog.stop();
    if(btn) btn.textContent='Adding to bag…';
    // Ring line = the SETTING only (metal + size). The diamond is its own cart
    // line, so we DON'T repeat it as a visible "Diamond" property here (that read
    // like two diamonds). The underscore-prefixed props stay for bundling + the
    // Shopify admin order (hidden from the customer); the cert/spec also live on
    // the diamond product line itself.
    var items=[{id:parseInt(getRingVariant()),quantity:1,properties:{'_bundle_ref':bundleRef,'Ring Size':ringSize,'_diamond_cert':selected.certNum,'_diamond_id':(prep&&prep.offerId)||selected.nivodaId,'_hold_id':(prep&&prep.holdId)||''}}];
    // Triggered diamond product (carries the diamond's price through checkout)
    if(prep && prep.variantId){ items.push({id:parseInt(prep.variantId),quantity:1,properties:{'_bundle_ref':bundleRef,'_paired_ring':CFG.productId,'Certificate':(selected.certLab||'')+' #'+(selected.certNum||'')}}); }
    try{ var res=await fetch('/cart/add.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:items})}); if(!res.ok) throw new Error('cart '+res.status); document.dispatchEvent(new CustomEvent('cart:updated',{bubbles:true})); if(typeof window.openCart==='function'){window.openCart();if(btn){btn.disabled=false;updateAddLabel();}}else if(typeof window.Broadcast!=='undefined'){window.Broadcast.trigger('cart:open');if(btn){btn.disabled=false;updateAddLabel();}}else{window.location.href='/cart';} }
    catch(err){ if(btn){btn.textContent='Error — please try again';btn.disabled=false;setTimeout(updateAddLabel,3000);} }
    addingToCart=false;
  }
  function initInfoPopups(){
    // Generic (i) info popups. Buttons carry data-pop="<template id>"; the
    // template's HTML is shown in a shared, body-teleported overlay.
    var ov=$('ps-info-overlay'); if(!ov) return;
    if(ov.parentNode!==document.body) document.body.appendChild(ov); // escape nav stacking
    var body=ov.querySelector('.psinfo-content');
    var open=function(id){ var t=document.getElementById(id); if(!t||!body) return; body.innerHTML=t.innerHTML; ov.hidden=false; ov.classList.add('is-open'); document.body.style.overflow='hidden'; };
    var close=function(){ ov.classList.remove('is-open'); ov.hidden=true; document.body.style.overflow=''; };
    document.querySelectorAll('.ps-info[data-pop]').forEach(function(b){
      b.addEventListener('click', function(e){ e.preventDefault(); e.stopPropagation(); open(b.getAttribute('data-pop')); });
    });
    var c=ov.querySelector('.psinfo-close'); if(c) c.addEventListener('click', close);
    ov.addEventListener('click', function(e){ if(e.target===ov) close(); });
    document.addEventListener('keydown', function(e){ if(e.key==='Escape' && ov.classList.contains('is-open')) close(); });
  }
  function initSize(){
    var trigger=$('ps-ds-size-trigger'),list=$('ps-ds-size-list'),labelEl=$('ps-ds-size-label'),hidden=$('ps-ds-size'); if(!trigger||!list||!hidden) return;
    function sizeLabel(v){ return v==='N' ? 'N (average)' : v; }
    var close=function(){list.hidden=true;trigger.setAttribute('aria-expanded','false');};
    var open=function(){list.hidden=false;trigger.setAttribute('aria-expanded','true');var sel=list.querySelector('[role="option"][aria-selected="true"]'); if(sel&&sel.scrollIntoView) sel.scrollIntoView({block:'nearest'});};
    trigger.addEventListener('click',function(e){e.stopPropagation();list.hidden?open():close();});
    list.querySelectorAll('[role="option"]').forEach(function(opt){opt.addEventListener('click',function(){hidden.value=opt.dataset.value;labelEl.textContent=sizeLabel(opt.dataset.value);labelEl.style.color='#212121';list.querySelectorAll('[role="option"]').forEach(function(o){o.removeAttribute('aria-selected');});opt.setAttribute('aria-selected','true');close();});});
    document.addEventListener('click',function(e){if(!list.hidden && !e.target.closest('#ps-ds-size-wrap')) close();});
    // Default to the average size (N) when this ring offers it.
    var nOpt=list.querySelector('[role="option"][data-value="N"]');
    if(nOpt && !hidden.value){ hidden.value='N'; labelEl.textContent='N (average)'; labelEl.style.color='#212121'; nOpt.setAttribute('aria-selected','true'); }
  }
  function addSettingPriceLabel(){ var p=document.querySelector('.product__price'); if(!p||p.querySelector('.ps-ds__setting-label')) return; var l=document.createElement('span'); l.className='ps-ds__setting-label'; l.textContent='Setting Price: '; p.insertBefore(l,p.firstChild); }

  /* ---- Metal-colour gallery swap ------------------------------------------
     Image filenames carry yellow / white / rose / shared. On metal select, show
     only the chosen colour's images (+ shared). Rules built for a gradual rollout:
       • untagged images always show (so nothing breaks before tagging is done),
       • if a colour has NO images yet, the gallery is left untouched.
     Gallery is Broadcast's show/hide type (.product__slide + media--active/hidden). */
  function metalColour(metal){ var m=(metal||'').toLowerCase();
    if(m.indexOf('rose')>=0) return 'rose';
    if(m.indexOf('white')>=0||m.indexOf('platinum')>=0) return 'white';
    return 'yellow'; }
  function imgColour(src){ var f=(src||'').toLowerCase();
    if(f.indexOf('rose')>=0) return 'rose';
    if(f.indexOf('platinum')>=0||f.indexOf('white')>=0) return 'white';
    if(f.indexOf('yellow')>=0) return 'yellow';
    if(f.indexOf('shared')>=0) return 'shared';
    return null; } // untagged -> always shown
  function swapGalleryColour(colour){
    var slides=[].slice.call(document.querySelectorAll('.product__slide'));
    if(!slides.length) return;
    function srcOf(s){ var i=s.querySelector('img'); return i&&(i.currentSrc||i.getAttribute('src')||i.getAttribute('data-src')||''); }
    var cols=slides.map(function(s){ return imgColour(srcOf(s)); });
    function thumb(mediaId,hide){ var num=(mediaId||'').split('-').pop(); if(!num) return;
      [].forEach.call(document.querySelectorAll('[data-media-id$="-'+num+'"]'),function(el){
        if(el.classList.contains('product__slide')) return;
        var t=el.closest('[class*="thumb"]')||el; if(t) t.classList.toggle('ds-hide',hide); }); }
    if(cols.indexOf(colour)<0){ // colour not available yet -> show everything
      slides.forEach(function(s){ s.classList.remove('ds-hide'); thumb(s.getAttribute('data-media-id'),false); }); return; }
    var firstVisible=null;
    slides.forEach(function(s,i){ var c=cols[i]; var show=(c===colour||c==='shared'||c===null);
      s.classList.toggle('ds-hide',!show); thumb(s.getAttribute('data-media-id'),!show);
      if(show&&!firstVisible) firstVisible=s; });
    var active=document.querySelector('.product__slide.media--active');
    if(firstVisible&&(!active||active.classList.contains('ds-hide'))){
      slides.forEach(function(s){ s.classList.remove('media--active'); s.classList.add('media--hidden'); });
      firstVisible.classList.remove('media--hidden'); firstVisible.classList.add('media--active'); }
  }

  /* Build metal -> variant featured-media id. Our custom swatches bypass the
     theme's variant→image switching, so we drive the gallery ourselves. */
  function buildMetalMedia(){
    try {
      var handle = location.pathname.split('/products/')[1]; if(!handle) return; handle = handle.split('?')[0].split('/')[0];
      fetch('/products/'+handle+'.js').then(function(r){ return r.json(); }).then(function(p){
        var mi = (p.options||[]).map(function(o){return (o.name||o);}).findIndex(function(n){ return /metal/i.test(n); });
        (p.variants||[]).forEach(function(v){
          var metal = (v.options && (mi>=0 ? v.options[mi] : v.options[0])) || v.title;
          var src = v.featured_image && (v.featured_image.src || v.featured_image);
          if (metal && src) metalMedia[metal] = String(src);
        });
        // NB: we intentionally do NOT touch the gallery on load — every image keeps
        // its natural position and the theme's first image stays selected. Reordering
        // only happens when the shopper actively clicks a metal swatch (see wireMetalSwatches).
      }).catch(function(){});
    } catch(e){}
  }
  // Lead the gallery with the chosen metal's photo + caption it.
  // The metal photos usually ALREADY exist as gallery slides, so we bring the
  // matching slide to the FRONT (non-destructive: every image stays, nothing is
  // duplicated, the platinum/etc hero isn't lost). Only if a metal has no photo
  // of its own do we fall back to overwriting the first slide.
  function baseName(u){ return String(u||'').split('?')[0].split('/').pop().toLowerCase(); }

  // Caption + anchor it to the image's bottom-left corner + bring first slide into view (mobile).
  function captionSlide(slide, metal){
    if(getComputedStyle(slide).position === 'static') slide.style.position = 'relative';
    // Only ONE caption should exist across the gallery — move/reuse a single node.
    var cap = document.querySelector('.ps-metal-cap');
    if(!cap){ cap = document.createElement('span'); cap.className = 'ps-metal-cap';
      cap.style.cssText = 'position:absolute;z-index:4;background:#f5f2ec;color:#1a1a1a;font-size:11px;letter-spacing:.07em;text-transform:uppercase;padding:5px 10px;pointer-events:none;border:1px solid rgba(26,26,26,.10);'; }
    if(cap.parentElement !== slide) slide.appendChild(cap);
    cap.textContent = metal;
    var img = slide.querySelector('img');
    function placeCap(){
      try{
        var sr = slide.getBoundingClientRect(), ir = img.getBoundingClientRect();
        if(!ir.width) return;
        cap.style.left   = (Math.max(0, ir.left - sr.left) + 12) + 'px';
        cap.style.bottom = (Math.max(0, sr.bottom - ir.bottom) + 12) + 'px';
      }catch(e){}
    }
    placeCap();
    if(img && !img.complete){ img.addEventListener('load', placeCap, { once:true }); }
    setTimeout(placeCap, 250);
    // Mobile slider: scroll the (now first) slide into view.
    var sc = slide.parentElement;
    for(var i=0;i<6 && sc;i++){ if(sc.scrollWidth - sc.clientWidth > 20){ try{ sc.scrollTo({ left:0, behavior:'smooth' }); }catch(e){ sc.scrollLeft=0; } break; } sc = sc.parentElement; }
  }

  // Cover the FIRST/main gallery image with the chosen metal's photo. We do NOT
  // move any slides around — every other image keeps its position. (Per Emil: the
  // metal's photo may still also appear later in the strip as its own slide; the
  // full per-metal gallery swap is the future metafield phase.)
  function showVariantMedia(metal){
    var src = metalMedia[metal]; if(!src) return;
    if(src.indexOf('//')===0) src = 'https:'+src;
    var slide = document.querySelector('product-images .product__slide') || document.querySelector('.product__slide');
    if(!slide) return;
    slide.classList.remove('ds-hide','media--hidden'); slide.classList.add('media--active');
    var img = slide.querySelector('img'); if(!img){ captionSlide(slide, metal); return; }
    var finalSrc = src + (src.indexOf('?')>-1 ? '&' : '?') + 'width=1200';
    if(img.getAttribute('data-ps-metal') === metal){ captionSlide(slide, metal); return; }
    // Preload + decode off-screen, then swap in one paint — no blank-frame flicker.
    var pre = new Image();
    function applySwap(){
      img.removeAttribute('data-src'); img.removeAttribute('data-srcset'); img.removeAttribute('srcset');
      img.src = finalSrc;
      img.setAttribute('data-ps-metal', metal);
      captionSlide(slide, metal);
    }
    pre.src = finalSrc;
    if(pre.decode){ pre.decode().then(applySwap).catch(applySwap); }
    else { pre.onload = applySwap; pre.onerror = applySwap; }
  }

  /* Metal swatches: this template has no theme Buy-Buttons block, so the metal
     radios are inert (price/variant never update). Drive them ourselves. */
  function wireMetalSwatches(){
    var form=document.querySelector('form[action*="/cart/add"]');
    var idIn=form&&form.querySelector('input[name="id"]');
    var radios=document.querySelectorAll('input[type="radio"][name^="options["]');
    if(!radios.length) return;
    function apply(metal){
      var vid=variantIdByTitle[metal];
      if(vid!=null){
        if(idIn) idIn.value=vid;
        var pence=ringVariants[String(vid)];
        var pe=document.querySelector('.product__price');
        if(pe && pence!=null){ pe.innerHTML='<span class="ps-ds__setting-label">Setting Price: </span>'+fmtPresentmentCents(pence); }
        var lbl=document.querySelector('.radio__legend__value'); if(lbl) lbl.textContent=metal;
        updateAddLabel();
      }
      showVariantMedia(metal);   // swap the main image to this metal's variant image
    }
    [].forEach.call(radios,function(r){ r.addEventListener('change',function(){ if(r.checked) apply(r.value); }); });
  }

  /* ===================== init =========================================== */
  // A ring on this template has NO native buy button — if the pool is empty/stale
  // or the selector errors, the page must NEVER be a dead end. Show a warm
  // hand-off to the humans instead.
  function renderNoStones(){
    try{
      var box=$('ps-ds-list'); if(!box) return;
      box.innerHTML='<div style="border:1px solid #e0dcd6;background:#fff;padding:16px 18px;font-size:13.5px;line-height:1.6;color:#3a352f;">'+
        'Our live diamond list for this design is being refreshed and will be back shortly.<br>'+
        'Prefer a hand-picked selection? <a href="/pages/book-appointment" style="color:#9a7b4f;text-decoration:underline;">Book a free appointment</a> '+
        'or call <a href="tel:01273846338" style="color:#9a7b4f;text-decoration:underline;">01273 846338</a> and our designers will source the perfect stone for you.</div>';
      var more=$('ps-ds-viewall'); if(more) more.style.display='none';
    }catch(e){}
  }
  function init(){
    ROOT = $('ps-diamond-selector'); if(!ROOT) return;
    try{ initInner(); }
    catch(e){
      // Any selector failure -> fallback UI rather than a silent, unbuyable page.
      try{ console.error('diamond-selector init failed:', e); }catch(_){}
      renderNoStones();
    }
  }
  function initInner(){
    CFG = getCfg();
    try { var de=$('ps-diamonds-data'); if(de) allDiamonds=JSON.parse(de.textContent)||[]; } catch(e){}
    try { var rv=$('ps-ring-variants'); if(rv) ringVariants=JSON.parse(rv.textContent)||{}; } catch(e){}
    try { var rvi=$('ps-ring-variant-ids'); if(rvi) variantIdByTitle=JSON.parse(rvi.textContent)||{}; } catch(e){}
    buildMetalMedia(); // map each metal to its variant's featured image so picking a metal swaps the gallery
    matched = capPerRungType(filter(CFG), DISPLAY_PER_RUNG);
    if(matched.length){ var labs=matched.filter(isLab); recommended=byPreference(labs.length?labs:matched,CFG)[0]; selected=recommended; }
    // Lab/natural filter lives only in the gallery (View all stones) to keep the
    // PDP light. The PDP list stays on the default 'all' view (recommended lab +
    // a natural beneath it).
    renderList();
    if(!matched.length) renderNoStones();
    if(selected){ var a=$('ps-ds-actions'); if(a) a.style.display=''; updateAddLabel(); }

    overlay=$('ds-overlay'); viewAll=$('ds-view-all'); viewDetail=$('ds-view-detail'); backBtn=$('ds-back'); titleEl=$('ds-title');
    if(overlay && overlay.parentNode!==document.body) document.body.appendChild(overlay); // escape nav stacking

    var more=$('ps-ds-viewall'); if(more) more.addEventListener('click', openAll);
    if(backBtn) backBtn.addEventListener('click', openAll);
    var close=$('ds-close'); if(close) close.addEventListener('click', closeOverlay);
    if(overlay) overlay.addEventListener('click', function(e){ if(e.target===overlay) closeOverlay(); });
    document.addEventListener('keydown', function(e){ if(e.key==='Escape' && overlay && overlay.classList.contains('is-open')) closeOverlay(); });
    window.addEventListener('resize', function(){ if(overlay && overlay.classList.contains('is-open') && !viewDetail.hidden) placeHand(); });

    initSize();
    initInfoPopups();
    wireMetalSwatches();
    var add=$('ps-ds-add-btn'); if(add) add.addEventListener('click', addToBag);
    var priceEl=document.querySelector('.product__price'); if(priceEl){ addSettingPriceLabel(); new MutationObserver(addSettingPriceLabel).observe(priceEl,{childList:true}); }
    var form=document.querySelector('form[action="/cart/add"]'); if(form) form.addEventListener('change', function(){ setTimeout(updateAddLabel,60); });
  }

  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded', init); else init();
})();

/* ===========================================================================
 * Cart hold-release watcher (runs site-wide — this file is loaded on every page
 * via theme.liquid, so it covers the cart drawer/page wherever removal happens).
 * Releases the Nivoda hold the moment a shopper removes a diamond from the bag.
 *   - First read only SEEDS the snapshot (never cancels).
 *   - Only re-checks after an explicit cart mutation (/cart/change|update|clear),
 *     NOT on navigation — so completing checkout (cart cleared server-side, off
 *     this page) never cancels a just-ordered hold.
 *   - cancel-hold is best-effort + soft-fails; holds also auto-expire at 24h.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.__nivodaCartHolds) return; // guard against double-load
  window.__nivodaCartHolds = true;

  var API = (window.__NIVODA_API || 'https://nivoda-api.vercel.app').replace(/\/$/, '');
  // The HOLD id rides on the ring line; the priced DIAMOND product line is marked
  // by _paired_ring. The two share a _bundle_ref. We release the hold when a
  // bundle's DIAMOND line leaves the cart (the shopper dropped the diamond) — even
  // if the setting stays — and NOT when only the setting is removed.
  var prevHoldOf = {}, prevHasDiamond = {};  // both keyed by _bundle_ref
  var seeded = false;                         // first read seeds, never cancels

  function snap(cart) {
    var holdOf = {}, hasDiamond = {};
    ((cart && cart.items) || []).forEach(function (it) {
      var p = it.properties || {}; var b = p._bundle_ref; if (!b) return;
      if (p._hold_id) holdOf[b] = p._hold_id;     // ring line carries the hold id
      if (p._paired_ring) hasDiamond[b] = true;   // the priced diamond product line
    });
    return { holdOf: holdOf, hasDiamond: hasDiamond };
  }
  function cancel(holdId) {
    try {
      fetch(API + '/api/diamond/cancel-hold', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ holdId: holdId }), keepalive: true
      });
    } catch (e) { /* best-effort */ }
  }
  var checking = false;
  function check() {
    if (checking) return; checking = true;
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var now = snap(cart);
        if (seeded) {
          Object.keys(prevHoldOf).forEach(function (b) {
            if (prevHasDiamond[b] && !now.hasDiamond[b]) cancel(prevHoldOf[b]); // diamond left -> release
          });
        }
        prevHoldOf = now.holdOf; prevHasDiamond = now.hasDiamond; seeded = true;
      })
      .catch(function () {})
      .then(function () { checking = false; });
  }
  check(); // seed once (no cancels)

  /* ---- Cart-page bundle re-validation (stale carts) -----------------------
     Engagement shoppers come back days later: the 24h hold has expired and the
     triggered product may have been archived (checkout silently drops archived
     lines — they'd buy the ring with no diamond). On every cart-page view,
     re-validate each bundle with the proxy: still available -> fresh hold +
     product revived, all silent; sold -> remove the bundle and say so. */
  function escTxt(s){ return String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;'); }
  function showGoneBanner(info){
    try{
      var main=document.querySelector('main')||document.body;
      var d=document.createElement('div');
      d.style.cssText='margin:14px auto;max-width:920px;font-size:13.5px;line-height:1.55;padding:12px 16px;border-left:3px solid #a23b2e;background:#faf8f4;color:#3a352f;';
      d.innerHTML='<b>'+escTxt((info&&info.title)||'The diamond in your bag')+'</b> was reserved by another customer before you checked out, so we’ve removed it from your bag. '+
        ((info&&info.url)?('<a href="'+escTxt(info.url)+'" style="color:#9a7b4f;text-decoration:underline;">Choose another stone for your ring &rarr;</a>'):'Please choose another stone.');
      main.insertBefore(d, main.firstChild);
    }catch(e){}
  }
  function revalidateCartBundles(){
    if(!/^\/cart\/?$/.test(location.pathname)) return;
    try{
      var flag=sessionStorage.getItem('psDsGone');
      if(flag){ sessionStorage.removeItem('psDsGone'); showGoneBanner(JSON.parse(flag)); }
    }catch(e){}
    origFetch('/cart.js',{headers:{'Accept':'application/json'}})
      .then(function(r){ return r.json(); })
      .then(function(cart){
        var bundles={};
        ((cart&&cart.items)||[]).forEach(function(it){
          var p=it.properties||{}; var b=p._bundle_ref; if(!b) return;
          var o=bundles[b]=bundles[b]||{keys:[]};
          o.keys.push(it.key);
          if(p._diamond_cert){ o.cert=p._diamond_cert; o.ringUrl=it.url; }
          if(p._paired_ring){ o.productId=it.product_id; o.diamondTitle=it.product_title; }
        });
        Object.keys(bundles).forEach(function(b){
          var o=bundles[b]; if(!o.cert||!o.productId) return; // ring-only/malformed -> leave alone
          // Only re-validate STALE bundles (>20h — the hold lives 24h). A fresh
          // bundle still carries OUR OWN hold, which Nivoda reports as
          // "unavailable" — re-checking it would nuke the shopper's own cart.
          // The _bundle_ref embeds its creation time: 'ps-<Date.now()>'.
          var m=/^ps-(\d+)$/.exec(b);
          var ageMs = m ? (Date.now()-Number(m[1])) : Infinity;
          if (ageMs < 20*3600*1000) return; // fresh -> leave alone, no Nivoda call
          origFetch(API+'/api/diamond/revalidate',{
            method:'POST',headers:{'Content-Type':'application/json'},
            body:JSON.stringify({certNumber:o.cert,productId:o.productId})
          }).then(function(r){ return r.json(); }).then(function(res){
            if(!res||res.ok!==false) return; // available / unverified -> cart untouched
            var updates={}; o.keys.forEach(function(k){ updates[k]=0; });
            origFetch('/cart/update.js',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({updates:updates})})
              .then(function(){
                try{ sessionStorage.setItem('psDsGone',JSON.stringify({title:o.diamondTitle||'',url:o.ringUrl||''})); }catch(e){}
                location.reload();
              }).catch(function(){});
          }).catch(function(){});
        });
      }).catch(function(){});
  }

  var origFetch = window.fetch;

  // Keep ring + diamond together: when the shopper removes EITHER line of a
  // bundle, remove its partner in the SAME update so the bag re-renders once
  // (no orphaned setting or diamond, no flicker) — and release the hold. The
  // Broadcast cart removes a line via fetch POST /cart/change {line, quantity:0}.
  function onRemove(args, body) {
    return origFetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var idx = Number(body.line);
        var line = (idx >= 1) ? cart.items[idx - 1]
          : cart.items.filter(function (i) { return i.key === body.id || String(i.variant_id) === String(body.id); })[0];
        var b = line && line.properties && line.properties._bundle_ref;
        if (!b) return origFetch.apply(window, args); // not a bundle line -> normal removal
        var updates = {}, hold = null;
        cart.items.forEach(function (i) {
          var p = i.properties || {};
          if (p._bundle_ref === b) { updates[i.key] = 0; if (p._hold_id) hold = p._hold_id; }
        });
        if (hold) cancel(hold); // release the diamond's Nivoda hold as the pair is removed
        return origFetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (resp) { setTimeout(check, 200); return resp; });
      });
  }

  if (typeof origFetch === 'function') {
    window.fetch = function () {
      var args = arguments, a0 = args[0];
      var url = (typeof a0 === 'string') ? a0 : (a0 && a0.url) || '';
      var init = args[1];
      if (/\/cart\/change(\.js)?($|\?)/.test(url) && init && typeof init.body === 'string') {
        var body = null; try { body = JSON.parse(init.body); } catch (e) {}
        if (body && Number(body.quantity) === 0 && (body.line != null || body.id != null)) {
          return onRemove(args, body).catch(function () { return origFetch.apply(window, args); });
        }
      }
      var pr = origFetch.apply(this, args);
      if (/\/cart\/(change|update|clear|add)(\.js)?/i.test(url)) { pr.then(function () { setTimeout(check, 150); }).catch(function () {}); }
      return pr;
    };
  }
  document.addEventListener('cart:updated', function () { setTimeout(check, 150); });
  document.addEventListener('cart:refresh', function () { setTimeout(check, 150); });
  revalidateCartBundles(); // cart page only — no-op elsewhere
})();

/* ===========================================================================
 * Site-wide swatch highlight (runs on every page — this file loads in theme.liquid).
 * The theme's "selected" colour-swatch ring is nearly invisible; give the chosen
 * swatch a clear gold ring (cream gap + gold) so the selection is obvious. Targets
 * only colour swatches (label[data-swatch]) — not size/other option radios.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.__psSwatchHighlight) return; // guard against double-load
  window.__psSwatchHighlight = true;

  // Lay the product trust-badge list out in 2 columns (it's a single block,
  // `.block__icon__row`, used only on the product page — not the mobile menu) so
  // the badges take ~half the vertical space.
  if (!document.getElementById('ps-site-css')) {
    var st = document.createElement('style'); st.id = 'ps-site-css';
    st.textContent = '.block__icon__row{display:grid;grid-template-columns:1fr 1fr;column-gap:20px;row-gap:8px;align-items:center;}';
    (document.head || document.documentElement).appendChild(st);
  }

  var RING = '0 0 0 2px #fcfbf9, 0 0 0 4px #9a7b4f';
  function radioFor(lbl){
    var id = lbl.getAttribute('for');
    return (id && document.getElementById(id)) || lbl.querySelector('input[type="radio"]');
  }
  function apply(){
    var labels = document.querySelectorAll('label[data-swatch]');
    [].forEach.call(labels, function(lbl){
      var r = radioFor(lbl);
      lbl.style.boxShadow = (r && r.checked) ? RING : '';
    });
  }
  // Event delegation so it survives the theme re-rendering the swatch block.
  document.addEventListener('change', function(e){
    var t=e.target; if (t && t.name && /^options\[/.test(t.name)) setTimeout(apply, 0);
  });
  document.addEventListener('click', function(e){
    if (e.target && e.target.closest && e.target.closest('label[data-swatch]')) setTimeout(apply, 60);
  });
  function boot(){ apply(); setTimeout(apply, 400); setTimeout(apply, 1200); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
